import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LandingPageOld = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: '💬',
      badge: 'popular',
      badgeText: '인기',
      title: t('landing.community.title', '커뮤니티'),
      description: t('landing.community.description', '뉴스 브리핑, 유튜브 트렌드, 자유 게시판'),
      link: 'https://community.ilouli.com'
    },
    {
      icon: '👨‍👩‍👧‍👦',
      badge: 'family',
      badgeText: 'Family',
      title: t('landing.familySpace.title', '가족 공간'),
      description: t('landing.familySpace.description', '가족 캘린더, 공유 공간'),
      link: 'https://family.ilouli.com'
    },
    {
      icon: '📊',
      badge: null,
      badgeText: null,
      title: t('landing.stock.title', '주식 정보'),
      description: t('landing.stock.description', '관심 종목 분석과 시장 정보'),
      link: 'https://stock.ilouli.com'
    }
  ];

  const services = [
    {
      icon: '📰',
      title: '뉴스 브리핑',
      description: 'AI가 정리한 오늘의 뉴스',
      link: 'https://community.ilouli.com/news'
    },
    {
      icon: '📺',
      title: '유튜브 트렌드',
      description: '인기 영상과 키워드 분석',
      link: 'https://community.ilouli.com/youtube'
    },
    {
      icon: '🎮',
      title: '미니게임',
      description: '심심풀이 게임과 랭킹',
      link: 'https://community.ilouli.com/games'
    },
    {
      icon: '📊',
      title: '주식 정보',
      description: '관심 종목 분석 도구',
      link: 'https://stock.ilouli.com'
    }
  ];

  // Badge color styles
  const badgeStyles = {
    popular: 'bg-[#FEF3C7] text-[#B45309]',
    family: 'bg-[#EDE9FE] text-[#7C3AED]',
    new: 'bg-[#EFF6FF] text-[#2563EB]'
  };

  return (
    <div className="max-w-[1100px] mx-auto p-8 relative z-[1] md:p-4 sm:p-3">
      {/* Hero Section */}
      <header className="text-center py-12 px-4 mb-10 md:py-8 md:px-2 md:mb-6">
        <h1 className="text-5xl font-semibold mb-3 tracking-tight text-[var(--color-text-primary,#1d1d1f)] md:text-[2rem] sm:text-[1.75rem]">
          ilouli.com
        </h1>
        <p className="text-xl text-[var(--color-text-secondary,#666666)] font-normal leading-relaxed max-w-[600px] mx-auto md:text-base md:px-2 sm:text-[0.95rem]">
          {t('landing.subtitle', '가족과 함께하는 AI 기반 콘텐츠 플랫폼')}
        </p>

        <div className="flex justify-center gap-4 mt-8 flex-wrap md:flex-col md:gap-3">
          <a
            href="https://community.ilouli.com"
            className="inline-flex items-center gap-2 py-3.5 px-6 bg-[#1d1d1f] text-white border-none rounded-[10px] text-base font-medium cursor-pointer transition-all duration-200 no-underline hover:bg-[#333333] hover:-translate-y-0.5 hover:text-white active:translate-y-0 md:w-full md:justify-center md:py-4"
          >
            커뮤니티 바로가기
          </a>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 py-3.5 px-6 bg-transparent text-[#1d1d1f] border border-[#e5e5e5] rounded-[10px] text-base font-medium cursor-pointer transition-all duration-200 no-underline hover:bg-[#f5f5f5] hover:text-[#1d1d1f] active:translate-y-0 md:w-full md:justify-center md:py-4"
          >
            서비스 소개
          </Link>
        </div>
      </header>

      {/* Features Grid */}
      <main className="grid grid-cols-3 gap-6 mb-12 lg:grid-cols-2 md:grid-cols-1 md:gap-4">
        {features.map((feature, index) => (
          <a
            key={index}
            href={feature.link}
            className="block p-7 rounded-xl bg-white border border-[#e5e5e5] transition-all duration-200 cursor-pointer no-underline hover:border-[#ccc] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:-translate-y-[3px] active:translate-y-0 md:p-5 sm:p-4"
          >
            {feature.badge && (
              <span className={`inline-block py-1 px-2 text-xs font-medium rounded mb-3 ${badgeStyles[feature.badge] || ''}`}>
                {feature.badgeText}
              </span>
            )}
            <div className="text-[2rem] mb-4 sm:text-2xl sm:mb-3">{feature.icon}</div>
            <h2 className="text-xl mb-2 font-semibold text-[var(--color-text-primary,#1d1d1f)] md:text-[1.1rem]">
              {feature.title}
            </h2>
            <p className="text-[0.95rem] text-[var(--color-text-secondary,#666666)] leading-relaxed m-0 md:text-[0.9rem]">
              {feature.description}
            </p>
          </a>
        ))}
      </main>

      {/* Services Section */}
      <section className="mb-12">
        <div className="mb-6 md:mb-4">
          <h2 className="text-2xl font-semibold mb-2 text-[var(--color-text-primary,#1d1d1f)] md:text-xl">
            빠른 서비스
          </h2>
          <p className="text-[0.95rem] text-[var(--color-text-secondary,#666666)] m-0">
            자주 찾는 서비스에 바로 접근하세요
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {services.map((service, index) => (
            <a
              key={index}
              href={service.link}
              className="flex items-center justify-between py-4 px-5 bg-white border border-[#e5e5e5] rounded-[10px] cursor-pointer transition-all duration-200 no-underline hover:border-[#ccc] hover:bg-[#fafafa] active:bg-[#f5f5f5] md:py-3.5 md:px-4"
            >
              <div className="flex items-center gap-4 sm:gap-3">
                <div className="text-2xl w-10 h-10 flex items-center justify-center bg-[#f5f5f7] rounded-[10px] md:w-9 md:h-9 md:text-xl">
                  {service.icon}
                </div>
                <div>
                  <h3 className="text-base font-medium m-0 mb-1 text-[var(--color-text-primary,#1d1d1f)] md:text-[0.95rem]">
                    {service.title}
                  </h3>
                  <p className="text-[0.85rem] text-[var(--color-text-secondary,#666666)] m-0 md:text-[0.8rem]">
                    {service.description}
                  </p>
                </div>
              </div>
              <span className="text-[#999] text-xl">›</span>
            </a>
          ))}
        </div>
      </section>

      {/* Footer Info */}
      <footer className="text-center py-8 px-4 border-t border-[#e5e5e5] mt-8">
        <p className="text-[0.85rem] text-[var(--color-text-tertiary,#767676)] m-0">
          © 2026 ilouli.com - Made with love for family
        </p>
      </footer>
    </div>
  );
};

export default LandingPageOld;
